<script setup lang="ts">
import type { Category, Content } from '~/types/blog'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { normalizeFieldBool, normalizeFieldJson, normalizeFieldNumber, normalizeFieldText } from '~/composables/useCustomFields'
import { buildPostPath } from '~/composables/usePermalink'
import { resolvePostCover } from '~/composables/usePostCover'
import { toPlainExcerpt } from '~/composables/usePlainText'

type FeaturedEntry = {
  slug?: string
  title?: string
  description?: string
  image?: string
  url?: string
}

type FeaturedCard = {
  title: string
  description: string
  image: string
  href: string
  sourceSlug?: string
}

type HomeListRestoreSnapshot = {
  key: string
  posts: Content[]
  total: number
  nextPage: number
  scrollY: number
  savedAt: number
}

const blogApi = useBlogApi()
const route = useRoute()
const router = useRouter()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)
const fields = siteData?.setting?.customFields
const showNotice = computed(() => normalizeFieldBool(fields, 'showNotice', true))
const noticeTitle = computed(() => normalizeFieldText(fields, 'noticeTitle', '公告'))
const noticeContent = computed(() => normalizeFieldText(fields, 'noticeContent', ''))
const noticeLines = computed(() =>
  String(noticeContent.value || '')
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean)
)

const SEARCH_SORT_OPTIONS = ['relevance', 'newest', 'hot'] as const
type SearchSortBy = typeof SEARCH_SORT_OPTIONS[number]
type SearchFilterForm = {
  keyword: string
  sortBy: SearchSortBy
  categoryId: string
}

const normalizeSearchSortBy = (raw: unknown, hasKeyword: boolean): SearchSortBy => {
  const value = String(raw || '').trim().toLowerCase()
  if (SEARCH_SORT_OPTIONS.includes(value as SearchSortBy)) {
    return value as SearchSortBy
  }
  return hasKeyword ? 'relevance' : 'newest'
}

const parsePositiveIntQuery = (raw: unknown): number => {
  const value = Number(raw || 0)
  if (!Number.isFinite(value) || value <= 0) {
    return 0
  }
  return Math.floor(value)
}

const sanitizeKeywordQuery = (raw: unknown): string => String(raw || '').trim()

const keyword = computed(() => String(route.query.keyword || '').trim())
const isSearchMode = computed(() => keyword.value.length > 0)
const activeSortBy = computed<SearchSortBy>(() => normalizeSearchSortBy(route.query.sortBy, isSearchMode.value))
const activeCategoryId = computed(() => parsePositiveIntQuery(route.query.categoryId))
const homePostPageSize = computed(() => {
  const size = Math.floor(normalizeFieldNumber(fields, 'homePostPageSize', 10))
  return Math.min(60, Math.max(1, size || 10))
})
const homeLatestListMode = computed(() => {
  const mode = normalizeFieldText(fields, 'homeLatestListMode', 'infinite').toLowerCase()
  return mode === 'pager' || mode === 'pagination' ? 'pager' : 'infinite'
})
const isPagerMode = computed(() => homeLatestListMode.value === 'pager')
const currentPage = computed(() => {
  const raw = Number(route.query.page || 1)
  if (!Number.isFinite(raw) || raw < 1) {
    return 1
  }
  return Math.floor(raw)
})
const requestPage = computed(() => (isPagerMode.value ? currentPage.value : 1))
const listRestoreKey = computed(
  () => `${homeLatestListMode.value}|${keyword.value}|${homePostPageSize.value}|${activeSortBy.value}|${activeCategoryId.value}`
)
const homeListRestore = useState<HomeListRestoreSnapshot | null>('home-list-restore', () => null)

const { data: firstPage, pending: firstPagePending } = await useAsyncData(
  () => `posts:first:${homeLatestListMode.value}:${keyword.value}:${requestPage.value}:${homePostPageSize.value}:${activeSortBy.value}:${activeCategoryId.value}`,
  () => blogApi.getContentMetaList({
    page: requestPage.value,
    pageSize: homePostPageSize.value,
    type: 'post',
    keyword: keyword.value,
    ...(isSearchMode.value
      ? {
          sortBy: activeSortBy.value,
          categoryId: activeCategoryId.value || undefined
        }
      : {})
  }),
  { watch: [keyword, requestPage, homeLatestListMode, homePostPageSize, activeSortBy, activeCategoryId] }
)

const { data: searchFilterSource } = await useAsyncData(
  () => `home:search-filter-source:${isSearchMode.value ? 'on' : 'off'}`,
  async () => {
    if (!isSearchMode.value) {
      return { categories: [] as Category[] }
    }
    const categories = await blogApi.getCategoryTree()
    return {
      categories: categories || []
    }
  },
  { watch: [isSearchMode] }
)

const flattenCategoryOptions = (nodes: Category[], prefix = ''): Array<{ ID: number; name: string }> => {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return []
  }
  const result: Array<{ ID: number; name: string }> = []
  const sorted = [...nodes].sort((a, b) => Number(a?.sort || 0) - Number(b?.sort || 0))
  for (const node of sorted) {
    if (!node?.ID) {
      continue
    }
    const name = String(node?.name || '').trim() || `分类 ${node.ID}`
    result.push({ ID: node.ID, name: `${prefix}${name}` })
    if ((node.children || []).length > 0) {
      result.push(...flattenCategoryOptions(node.children || [], `${prefix}${name} / `))
    }
  }
  return result
}

const searchCategoryOptions = computed(() => flattenCategoryOptions(searchFilterSource.value?.categories || []))

const searchForm = reactive<SearchFilterForm>({
  keyword: '',
  sortBy: 'relevance',
  categoryId: ''
})

const syncSearchFormFromRoute = () => {
  searchForm.keyword = keyword.value
  searchForm.sortBy = activeSortBy.value
  searchForm.categoryId = activeCategoryId.value > 0 ? String(activeCategoryId.value) : ''
}

syncSearchFormFromRoute()

watch(
  () => route.fullPath,
  () => {
    syncSearchFormFromRoute()
  }
)

const applySearchFilters = async () => {
  const nextQuery: Record<string, string> = {}
  const nextKeyword = sanitizeKeywordQuery(searchForm.keyword)
  if (nextKeyword) {
    nextQuery.keyword = nextKeyword
  }
  if (nextQuery.keyword) {
    nextQuery.sortBy = normalizeSearchSortBy(searchForm.sortBy, true)
    const categoryID = parsePositiveIntQuery(searchForm.categoryId)
    if (categoryID > 0) {
      nextQuery.categoryId = String(categoryID)
    }
  }
  await router.push({ path: '/', query: nextQuery })
}

const applyDropdownFilters = async () => {
  await applySearchFilters()
}

const resetDropdownFilters = async () => {
  searchForm.sortBy = 'relevance'
  searchForm.categoryId = ''
  await applySearchFilters()
}

const siteName = normalizeFieldText(fields, 'siteName', 'Yuelai Blog')
const siteDescription = normalizeFieldText(fields, 'siteDescription', normalizeFieldText(fields, 'seoDescription', '技术与研究内容博客'))
const seoKeywords = normalizeFieldText(fields, 'seoKeywords', 'blog,技术博客')
const homeFeaturedUseHotPosts = computed(() => normalizeFieldBool(fields, 'homeFeaturedUseHotPosts', false))
const featuredRaw = computed<FeaturedEntry[]>(() => {
  const entries = normalizeFieldJson<FeaturedEntry[]>(fields, 'homeFeaturedEntries', [])
  return entries
})
const featuredSlugSet = computed(() => {
  if (keyword.value || homeFeaturedUseHotPosts.value || featuredRaw.value.length !== 4) {
    return [] as string[]
  }
  const seen = new Set<string>()
  const result: string[] = []
  for (const entry of featuredRaw.value) {
    const slug = String(entry?.slug || '').trim()
    if (!slug || seen.has(slug)) {
      continue
    }
    seen.add(slug)
    result.push(slug)
  }
  return result
})
const featuredSlugKey = computed(() => featuredSlugSet.value.join('|'))
const { data: featuredHotPosts } = await useAsyncData(
  () => `home:featured-hot-posts:${homeFeaturedUseHotPosts.value ? 'on' : 'off'}:${keyword.value}`,
  async () => {
    if (!homeFeaturedUseHotPosts.value || keyword.value) {
      return [] as Content[]
    }
    return blogApi.getHotContentList({ limit: 4 })
  },
  { watch: [homeFeaturedUseHotPosts, keyword] }
)
const { data: featuredSourceMap } = await useAsyncData(
  () => `home:featured-source:${featuredSlugKey.value}`,
  async () => {
    if (!featuredSlugSet.value.length) {
      return {} as Record<string, Content>
    }
    const rows = await Promise.all(
      featuredSlugSet.value.map(async slug => {
        try {
          const post = await blogApi.getContentMetaBySlug(slug)
          return post?.type === 'post' ? ([slug, post] as const) : null
        } catch {
          return null
        }
      })
    )
    return Object.fromEntries(rows.filter(Boolean) as ReadonlyArray<readonly [string, Content]>)
  },
  { watch: [featuredSlugKey] }
)

const posts = ref<Content[]>([])
const total = ref(0)
const nextPage = ref(2)
const loadingMore = ref(false)
const sentinel = ref<HTMLElement | null>(null)
const restoredFromBack = ref(false)
const pendingRestoreScrollY = ref<number | null>(null)
let observer: IntersectionObserver | null = null

const featuredCards = computed<FeaturedCard[]>(() => {
  if (keyword.value) {
    return []
  }
  if (homeFeaturedUseHotPosts.value) {
    return (featuredHotPosts.value || []).slice(0, 4).map(item => ({
      title: String(item?.title || '无标题').trim(),
      description: toPlainExcerpt(item?.summary || item?.content || '', 120, ''),
      image: resolvePostCover(item?.cover, item),
      href: buildPostPath(fields, item, { category: item.categories?.[0]?.slug || '' }),
      sourceSlug: item.slug
    }))
  }
  if (featuredRaw.value.length !== 4) {
    return []
  }
  const source = featuredSourceMap.value || {}
  const cards = featuredRaw.value.map((entry, index) => {
    const slug = String(entry?.slug || '').trim()
    const image = String(entry?.image || '').trim()
    const jumpURL = String(entry?.url || '').trim()
    if (slug) {
      const found = source[slug]
      if (!found) {
        return null
      }
      return {
        title: String(entry?.title || found.title || '无标题').trim(),
        description: toPlainExcerpt(entry?.description || found.summary || found.content || '', 120, ''),
        image: image ? resolveImageAssetUrl(image) : resolvePostCover(found.cover, found),
        href: jumpURL || buildPostPath(fields, found, { category: found.categories?.[0]?.slug || '' }),
        sourceSlug: found.slug
      }
    }
    if (image && jumpURL) {
      return {
        title: String(entry?.title || `推荐 ${index + 1}`).trim(),
        description: toPlainExcerpt(entry?.description || '', 120, ''),
        image: resolveImageAssetUrl(image),
        href: jumpURL
      }
    }
    return null
  })
  if (cards.some(item => !item)) {
    return []
  }
  return cards as FeaturedCard[]
})

const loadedFeaturedImages = reactive(new Set<string>())
// featuredImageKey 为推荐卡片图片加载状态提供稳定标识。
const featuredImageKey = (item: FeaturedCard) => `${item.href}|${item.image}`
// markFeaturedImageLoaded 在图片成功加载后结束推荐卡片骨架状态。
const markFeaturedImageLoaded = (item: FeaturedCard) => {
  loadedFeaturedImages.add(featuredImageKey(item))
}
// isFeaturedImageLoaded 判断推荐卡片图片是否已结束加载。
const isFeaturedImageLoaded = (item: FeaturedCard) => loadedFeaturedImages.has(featuredImageKey(item))

const visiblePosts = computed(() => posts.value)
const showInitialPostSkeleton = computed(() => firstPagePending.value)

const hasMore = computed(() => !isPagerMode.value && posts.value.length < total.value)

const sortPosts = (list: Content[]) => {
  if (isSearchMode.value) {
    return [...list]
  }
  return [...list].sort((a, b) => {
    const aTop = Boolean(a?.isTop)
    const bTop = Boolean(b?.isTop)
    if (aTop !== bTop) {
      return aTop ? -1 : 1
    }
    const aTime = a?.publishedAt ? new Date(a.publishedAt).getTime() : 0
    const bTime = b?.publishedAt ? new Date(b.publishedAt).getTime() : 0
    if (aTime !== bTime) {
      return bTime - aTime
    }
    return (b?.ID || 0) - (a?.ID || 0)
  })
}

const resetFromFirstPage = () => {
  const payload = firstPage.value
  posts.value = sortPosts(payload?.list || [])
  total.value = payload?.total || 0
  nextPage.value = requestPage.value + 1
}

const restoreListSnapshot = () => {
  if (!import.meta.client || isPagerMode.value) {
    return false
  }
  const snapshot = homeListRestore.value
  if (!snapshot || snapshot.key !== listRestoreKey.value || !Array.isArray(snapshot.posts) || snapshot.posts.length === 0) {
    return false
  }
  posts.value = sortPosts(snapshot.posts)
  total.value = Math.max(Number(snapshot.total || 0), posts.value.length)
  nextPage.value = Math.max(2, Number(snapshot.nextPage || 2))
  pendingRestoreScrollY.value = Math.max(0, Number(snapshot.scrollY || 0))
  restoredFromBack.value = true
  return true
}

restoreListSnapshot()

watch(
  () => firstPage.value,
  () => {
    if (restoredFromBack.value) {
      restoredFromBack.value = false
      return
    }
    resetFromFirstPage()
  },
  { immediate: true }
)

const loadMore = async () => {
  if (isPagerMode.value || loadingMore.value || !hasMore.value) {
    return
  }
  loadingMore.value = true
  try {
    const data = await blogApi.getContentMetaList({
      page: nextPage.value,
      pageSize: homePostPageSize.value,
      type: 'post',
      keyword: keyword.value,
      ...(isSearchMode.value
        ? {
            sortBy: activeSortBy.value,
            categoryId: activeCategoryId.value || undefined
          }
        : {})
    })
    posts.value = sortPosts([...posts.value, ...(data.list || [])])
    total.value = data.total || total.value
    nextPage.value += 1
  } finally {
    loadingMore.value = false
  }
}

const bindObserver = () => {
  if (!import.meta.client) {
    return
  }
  if (isPagerMode.value) {
    observer?.disconnect()
    observer = null
    return
  }
  if (observer) {
    observer.disconnect()
    observer = null
  }
  if (!sentinel.value || !hasMore.value) {
    return
  }
  observer = new IntersectionObserver(
    entries => {
      if (entries[0]?.isIntersecting) {
        void loadMore()
      }
    },
    { rootMargin: '260px 0px' }
  )
  observer.observe(sentinel.value)
}

onMounted(() => {
  if (pendingRestoreScrollY.value !== null) {
    const scrollY = pendingRestoreScrollY.value
    pendingRestoreScrollY.value = null
    nextTick(() => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: scrollY, behavior: 'auto' })
        })
      })
    })
  }
  bindObserver()
})

watch([sentinel, hasMore, () => keyword.value, isPagerMode], () => {
  bindObserver()
})

onBeforeUnmount(() => {
  observer?.disconnect()
})

onBeforeRouteLeave(() => {
  if (!import.meta.client) {
    return
  }
  if (isPagerMode.value) {
    homeListRestore.value = null
    return
  }
  homeListRestore.value = {
    key: listRestoreKey.value,
    posts: [...posts.value],
    total: total.value,
    nextPage: nextPage.value,
    scrollY: window.scrollY || 0,
    savedAt: Date.now()
  }
})

useSeoMeta({
  title: keyword.value ? `${keyword.value} - ${siteName}` : `${siteName}`,
  description: siteDescription,
  keywords: seoKeywords,
  ogTitle: keyword.value ? `${keyword.value} - ${siteName}` : `${siteName}`,
  ogDescription: siteDescription
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})
</script>

<template>
  <section class="home-page">
    <section v-if="featuredCards.length" class="featured-layout">
      <NuxtLink :to="featuredCards[0].href" class="card featured-main">
        <div
          class="featured-cover album-cover"
          :class="{ 'is-loaded': !featuredCards[0].image || isFeaturedImageLoaded(featuredCards[0]) }"
        >
          <BlogImage
            v-if="featuredCards[0].image"
            :src="featuredCards[0].image"
            :alt="featuredCards[0].title"
            class="featured-image album-image"
            loading="eager"
            fetchpriority="high"
            @load="markFeaturedImageLoaded(featuredCards[0])"
          />
          <div v-else class="featured-placeholder" aria-hidden="true">
            <i class="fa-regular fa-image" />
          </div>
        </div>
        <div class="featured-caption">
          <h2>{{ featuredCards[0].title }}</h2>
          <p v-if="featuredCards[0].description">{{ featuredCards[0].description }}</p>
        </div>
      </NuxtLink>
      <div class="featured-side">
        <NuxtLink v-for="(item, idx) in featuredCards.slice(1)" :key="`featured:${idx}`" :to="item.href" class="card featured-mini">
          <div
            class="featured-cover album-cover"
            :class="{ 'is-loaded': !item.image || isFeaturedImageLoaded(item) }"
          >
            <BlogImage
              v-if="item.image"
              :src="item.image"
              :alt="item.title"
              class="featured-image album-image"
              @load="markFeaturedImageLoaded(item)"
            />
            <div v-else class="featured-placeholder" aria-hidden="true">
              <i class="fa-regular fa-image" />
            </div>
          </div>
          <div class="featured-caption">
            <h3>{{ item.title }}</h3>
            <p v-if="item.description">{{ item.description }}</p>
          </div>
        </NuxtLink>
      </div>
    </section>

    <section v-if="showNotice" class="card mobile-notice">
      <h3><i class="fa-regular fa-bell" aria-hidden="true" />{{ noticeTitle }}</h3>
      <ul v-if="noticeLines.length" class="notice-list">
        <li v-for="(line, idx) in noticeLines" :key="idx">{{ line }}</li>
      </ul>
      <div v-else class="empty-card">暂无公告</div>
    </section>

    <section v-if="isSearchMode" class="card search-panel">
      <div class="search-panel-head">
        <h3><i class="fa-solid fa-magnifying-glass" aria-hidden="true" />搜索结果</h3>
        <span>关键词 "{{ keyword }}" · 共 {{ total }} 条</span>
      </div>
      <form class="search-panel-form" @submit.prevent="applySearchFilters">
        <label class="search-field search-field-keyword">
          <span>关键词</span>
          <input v-model.trim="searchForm.keyword" type="text" placeholder="输入关键词" />
        </label>
        <label class="search-field search-field-sort">
          <span>排序</span>
          <div class="search-select-wrap">
            <select v-model="searchForm.sortBy" @change="applyDropdownFilters">
              <option value="relevance">相关度</option>
              <option value="newest">最新发布</option>
              <option value="hot">热度</option>
            </select>
            <i class="fa-solid fa-chevron-down search-select-icon" aria-hidden="true" />
          </div>
        </label>
        <label class="search-field search-field-category">
          <span>分类</span>
          <div class="search-select-wrap">
            <select v-model="searchForm.categoryId" @change="applyDropdownFilters">
              <option value="">全部分类</option>
              <option v-for="item in searchCategoryOptions" :key="item.ID" :value="String(item.ID)">
                {{ item.name }}
              </option>
            </select>
            <i class="fa-solid fa-chevron-down search-select-icon" aria-hidden="true" />
          </div>
        </label>
        <div class="search-actions">
          <button type="submit">筛选</button>
          <button type="button" class="search-reset-btn" @click="resetDropdownFilters">重置</button>
        </div>
      </form>
    </section>

    <section v-if="showInitialPostSkeleton || visiblePosts.length" class="posts-wrap">
      <BlogPostCardSkeleton v-if="showInitialPostSkeleton" :rows="Math.min(4, homePostPageSize)" />
      <template v-else>
        <BlogPostCard v-for="item in visiblePosts" :key="item.ID" :item="item" />
        <BlogPager
          v-if="isPagerMode"
          :page="currentPage"
          :page-size="homePostPageSize"
          :total="total"
        />
        <template v-else>
          <div ref="sentinel" class="scroll-sentinel" />
          <BlogPostCardSkeleton v-if="loadingMore" :rows="2" />
          <div v-else-if="!hasMore" class="load-end-tip">没有更多了</div>
        </template>
      </template>
    </section>

    <div v-else class="empty-card">暂无文章</div>
  </section>
</template>

<style scoped>
.home-page {
  display: grid;
  gap: 18px;
  min-width: 0;
}

.posts-wrap {
  display: grid;
  gap: 18px;
}

.search-panel {
  display: grid;
  gap: 12px;
  padding: 14px;
}

.search-panel-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}

.search-panel-head h3 {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
}

.search-panel-head span {
  color: var(--text-soft);
  font-size: 13px;
}

.search-panel-form {
  display: flex;
  align-items: end;
  gap: 12px;
  flex-wrap: nowrap;
  min-width: 0;
}

.search-field {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.search-field span {
  font-size: 12px;
  color: var(--text-soft);
  white-space: nowrap;
}

.search-field-keyword,
.search-field-sort,
.search-field-category {
  flex: 1 1 0;
}

.search-select-wrap {
  position: relative;
  width: 100%;
  min-width: 0;
}

.search-field input,
.search-field select {
  width: 100%;
  min-height: 36px;
  padding: 7px 34px 7px 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--bg-soft);
  color: var(--text);
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.search-field input {
  padding-right: 10px;
}

.search-field input:focus,
.search-field select:focus {
  outline: none;
  border-color: color-mix(in srgb, var(--brand) 55%, var(--line) 45%);
}

.search-select-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: var(--text-soft);
  pointer-events: none;
}

.search-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.search-actions button {
  min-height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid var(--brand);
  background: var(--brand);
  color: #fff;
  cursor: pointer;
}

.search-actions .search-reset-btn {
  border-color: var(--line);
  background: var(--bg-soft);
  color: var(--text);
}

.posts-wrap :deep(.post-card) {
  margin-bottom: 0;
}

.featured-layout {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  gap: 10px;
  height: clamp(300px, 36vw, 420px);
  max-height: 420px;
  min-width: 0;
}

.featured-main,
.featured-mini {
  position: relative;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  min-height: 0;
  color: #fff;
  text-decoration: none;
  background: var(--empty-bg);
  isolation: isolate;
}

.featured-main {
  height: 100%;
}

.featured-cover {
  position: absolute;
  inset: 0;
  height: 100%;
  --album-placeholder-height: 100%;
}

.featured-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.featured-placeholder {
  display: grid;
  width: 100%;
  height: 100%;
  place-items: center;
  color: color-mix(in srgb, var(--text-soft) 58%, transparent);
  font-size: 42px;
}

.featured-side {
  display: grid;
  gap: 10px;
  grid-template-rows: repeat(3, minmax(0, 1fr));
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
}

.featured-mini {
  height: 100%;
  min-width: 0;
}

.featured-caption {
  position: relative;
  z-index: 2;
  width: 100%;
  min-width: 0;
  display: grid;
  gap: 6px;
  padding: 12px;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.74) 100%);
}

.featured-caption h2,
.featured-caption h3 {
  margin: 0;
  min-width: 0;
  font-size: 20px;
  line-height: 1.35;
  word-break: break-word;
  overflow-wrap: anywhere;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.featured-caption h3 {
  font-size: 16px;
}

.featured-caption p {
  margin: 0;
  min-width: 0;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-word;
  overflow-wrap: anywhere;
  color: rgba(255, 255, 255, 0.86);
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.featured-mini .featured-caption h3 {
  -webkit-line-clamp: 1;
}

.featured-mini .featured-caption p {
  -webkit-line-clamp: 1;
}

.scroll-sentinel {
  height: 1px;
}

.load-end-tip {
  padding: 14px;
  text-align: center;
  color: var(--text-soft);
  font-size: 13px;
}

.mobile-notice {
  display: none;
  padding: 16px;
}

.mobile-notice h3 {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 18px;
}

.notice-list {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 8px;
}

.notice-list li {
  font-size: 13px;
  color: var(--text-soft);
}

@media (max-width: 960px) {
  .mobile-notice {
    display: block;
  }
}

@media (max-width: 760px) {
  .search-panel-form {
    flex-wrap: wrap;
    align-items: stretch;
  }

  .search-field-sort,
  .search-field-keyword,
  .search-field-category {
    flex: 1 1 100%;
  }

  .search-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .featured-layout {
    grid-template-columns: 1fr;
    min-height: 0;
    height: auto;
    max-height: none;
  }

  .featured-main {
    min-height: 220px;
  }

  .featured-side {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-template-rows: 1fr;
    height: auto;
    gap: 8px;
    padding-bottom: 0;
  }

  .featured-mini {
    width: 100%;
    height: 100%;
    min-height: 112px;
  }

  .featured-mini .featured-caption p {
    display: none;
  }
}

@media (max-width: 560px) {
  .featured-main {
    min-height: 180px;
  }

  .featured-side {
    gap: 6px;
  }

  .featured-caption {
    padding: 8px;
  }

  .featured-caption h3 {
    font-size: 14px;
  }
}
</style>
